import { Controller } from "@hotwired/stimulus"

// Keeps the read-only Pause (time_break) field in sync with the sum of the
// break-period rows (Pausenzeiten). Attach to a container that wraps both the
// time_break input and the break-period rows. The Pause input is found by name
// (ends with "[time_break]"); each row is marked with data-break-period-row and
// its inputs with data-break-from / data-break-to.
export default class extends Controller {
  connect() {
    this._recompute = this._recompute.bind(this)
    this.element.addEventListener("input", this._recompute)
    this.element.addEventListener("change", this._recompute)
    this.element.addEventListener("click", this._recompute)
    this._recompute()
  }

  disconnect() {
    this.element.removeEventListener("input", this._recompute)
    this.element.removeEventListener("change", this._recompute)
    this.element.removeEventListener("click", this._recompute)
  }

  _recompute() {
    const total = this.element.querySelector("input[name$='[time_break]']")
    if (!total) return

    let totalMinutes = 0
    this.element.querySelectorAll("[data-break-period-row]").forEach((row) => {
      const destroy = row.querySelector("input[name*='_destroy']")
      if (destroy && destroy.value === "1") return
      if (row.offsetParent === null) return // hidden (removed row)

      const from = this._minutes(row.querySelector("[data-break-from]"))
      const to = this._minutes(row.querySelector("[data-break-to]"))
      if (from === null || to === null || to <= from) return

      totalMinutes += to - from
    })

    const hours = String(Math.floor(totalMinutes / 60)).padStart(2, "0")
    const minutes = String(totalMinutes % 60).padStart(2, "0")
    const newValue = `${hours}:${minutes}`

    // Only write + notify on an actual change. The guard also stops a self-loop:
    // the dispatched event bubbles back to this controller's container, re-running
    // _recompute, which then sees the unchanged value and returns early.
    if (total.value === newValue) return
    total.value = newValue

    // Notify downstream listeners (e.g. multi-site booking net work time) that the
    // derived break total changed — assigning .value alone fires no DOM event.
    total.dispatchEvent(new Event("input", { bubbles: true }))
    // `change` as well: when time_break is a visible (read-only) picker rather than a plain
    // hidden field, this is the element the time-picker syncs its visible half off, so without
    // it the displayed Pause keeps showing the previous total.
    total.dispatchEvent(new Event("change", { bubbles: true }))
  }

  _minutes(input) {
    const value = input && input.value
    if (!value || !value.includes(":")) return null

    const [h, m] = value.split(":").map((n) => parseInt(n, 10))
    if (Number.isNaN(h) || Number.isNaN(m)) return null

    return h * 60 + m
  }
}
